import React from 'react';
import type {ActionMeta} from 'react-select';
import {SelectOption} from '@chekin/core';
import {Dropdown} from './components';
import {
  QueriedInfiniteScrollSelect,
  QueriedInfiniteScrollSelectProps,
} from '../QueriedInfiniteScrollSelect';
import {useModalControls, useOutsideClick} from '@chekin/guestapp-ui-kit';
import {SelectRefType} from '@guestapp/old-ui-kit';

type QueriedSearchingSelectProps<T, V, L> = QueriedInfiniteScrollSelectProps<T, V, L> & {
  trigger?: (isOpen: boolean, toggle: () => void) => React.ReactNode;
};

const SearchingSelectInternal = <T, V, L>(
  {
    fetcher,
    queryKey,
    trigger,
    className,
    onChange,
    ...props
  }: QueriedSearchingSelectProps<T, V, L>,
  ref: React.ForwardedRef<SelectRefType<T, V, L>>,
) => {
  const {isOpen, closeModal, toggleModal} = useModalControls();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  useOutsideClick(wrapperRef, closeModal);

  return (
    <Dropdown
      ref={wrapperRef}
      isOpen={isOpen}
      className={className}
      target={trigger?.(isOpen, toggleModal)}
    >
      <QueriedInfiniteScrollSelect
        ref={ref}
        fetcher={fetcher}
        queryKey={queryKey}
        backspaceRemovesValue={false}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        menuIsOpen
        isMenuOpen
        forcedSearchMode
        onChange={(
          option: SelectOption<T, V, L> | null,
          info: ActionMeta<SelectOption<T, V, L>>,
        ) => {
          onChange?.(option, info);
          closeModal();
        }}
        components={{LoadingIndicator: undefined, DropdownIndicator: null}}
        tabSelectsValue={false}
        listSizes={{height: 224}}
        isSearchable
        autoFocus
        {...props}
      />
    </Dropdown>
  );
};

const QueriedSearchingSelect = React.forwardRef(SearchingSelectInternal) as <T, V, L>(
  props: QueriedSearchingSelectProps<T, V, L> & {
    ref?: React.ForwardedRef<SelectRefType<T, V, L>>;
  },
) => ReturnType<typeof SearchingSelectInternal>;

export {QueriedSearchingSelect};
export type {QueriedSearchingSelectProps};
